import React, { useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import {
  DropdownLink,
  DropdownList,
  DropdownTrigger,
  IconChevronDown,
} from '@spotify-internal/encore-web';
import { CounterButton } from '@spotify-internal/encore-web/content-platform';
import { useT } from '@spotify-internal/i18n-core';
import { endOfToday, xDaysAgo } from '@helpers/dateFormatter';
import { setLanguagePreference } from '@helpers/translate';
import { DateRangePickerWrapper } from './DateRangePickerWrapper';
import { DateRange } from './types';
import { useA11yDropdown } from './useA11yDropdown';

const triggerId = 'date-range-dropdown-toggle';

enum DateRangeWindow {
  Last7Days = 'last7DaysId',
  Last28Days = 'last28DaysId',
  Last90Days = 'last90DaysId',
  CustomDateRange = 'customDateRangeId',
  AllTime = 'allTime',
}

const Container = styled.div`
  display: block;
  cursor: pointer;
`;

const DropdownTriggerStyled = styled(DropdownTrigger)`
  #${triggerId} {
    margin-left: 0px;
  }
`;

const DropdownStyled = styled(CounterButton)`
  span {
    font-size: 10px !important;
    padding: 0 !important;
    align-items: center;
    line-height: 16px;
    display: flex;
  }
`;

const MinWidthList = styled(DropdownList)`
  min-width: 170px;
`;

const dateRangeToDisplay = (
  dateRange: DateRange,
  customDateRangeText: String,
) => {
  const { startDate, endDate } = dateRange;
  if (!startDate || !endDate) return customDateRangeText;
  return `${startDate.format('L')} - ${endDate.format('L')}`;
};

interface DateRangeDropdownProps {
  createdAtRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
}

export const DateRangeDropdown = (props: DateRangeDropdownProps) => {
  const { locale } = useRouter();
  setLanguagePreference(locale);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const t = useT();
  const dropdownListId = 'date-range-listbox';
  const items = [
    {
      text: t(
        'atlas.date_range_types.last_7_days',
        `Last 7 days`,
        'Refers to previous 7 days',
      ),
      id: DateRangeWindow.Last7Days,
    },
    {
      text: t(
        'atlas.date_range_types.last_28_days',
        `Last 28 days`,
        'Refers to previous 28 days',
      ),
      id: DateRangeWindow.Last28Days,
    },
    {
      text: t(
        'atlas.date_range_types.last_90_days',
        `Last 90 days`,
        'Refers to previous 90 days',
      ),
      id: DateRangeWindow.Last90Days,
    },
    {
      text: t(
        'atlas.date_range_types.custom_date_range',
        `Custom date range`,
        'Refers to a custom date range',
      ),
      id: DateRangeWindow.CustomDateRange,
    },
    {
      text: t(
        'atlas.date_range_types.all_time',
        `All time`,
        'Refers to all time',
      ),
      id: DateRangeWindow.AllTime,
    },
  ];

  const { show, setShow, selected, setSelected, onClick, onHide } =
    useA11yDropdown({
      dropdownListId,
      triggerId,
      initialSelected: 4,
    });

  const onKeyDown = (
    e: React.KeyboardEvent,
    dropdownItems: { text: string; id: DateRangeWindow }[],
  ) => {
    const upArrow = 38;
    const downArrow = 40;
    const enter = 13;
    const escape = 27;
    const tab = 9;
    const space = 32;

    let item;
    const itemsLength = dropdownItems.length;

    switch (e.which) {
      case upArrow: {
        e.preventDefault();
        if (selected !== 0) {
          setSelected(selected - 1);
          item = dropdownItems[selected - 1];
          determineDateRange(item.id);
        }
        break;
      }

      case downArrow: {
        e.preventDefault();
        if (selected < itemsLength - 1) {
          setSelected(selected + 1);
          item = dropdownItems[selected + 1];
          determineDateRange(item.id);
        }
        break;
      }

      case enter:
      case escape:
      case space:
      case tab: {
        e.preventDefault();
        setShow(false);
        const element = document.getElementById(triggerId) as HTMLElement;
        element.focus();
        break;
      }

      default: {
        break;
      }
    }
  };

  function determineDateRange(id: string) {
    switch (id) {
      case DateRangeWindow.Last7Days:
        setShowDateRangePicker(false);
        props.onDateRangeChange({
          startDate: xDaysAgo(7),
          endDate: endOfToday(),
        });
        break;
      case DateRangeWindow.Last28Days:
        setShowDateRangePicker(false);
        props.onDateRangeChange({
          startDate: xDaysAgo(28),
          endDate: endOfToday(),
        });
        break;
      case DateRangeWindow.Last90Days:
        setShowDateRangePicker(false);
        props.onDateRangeChange({
          startDate: xDaysAgo(90),
          endDate: endOfToday(),
        });
        break;
      case DateRangeWindow.CustomDateRange:
        setShowDateRangePicker(true);
        break;
      case DateRangeWindow.AllTime:
        setShowDateRangePicker(false);
        props.onDateRangeChange({
          startDate: xDaysAgo(90),
          endDate: endOfToday(),
        });
        break;
      default:
        break;
    }
  }

  return (
    <>
      <Container>
        <DropdownTriggerStyled
          overlay={
            show && (
              <MinWidthList
                onBlur={() => setShow(false)}
                onKeyDown={e => {
                  onKeyDown(e, items);
                }}
                id={dropdownListId}
                aria-activedescendant={items[selected].id}
              >
                {items.map((item, index) => (
                  <DropdownLink
                    as="li"
                    role="option"
                    id={item.id}
                    key={item.id}
                    onFocus={() => setSelected(index)}
                    onClick={e => {
                      onClick(e, index);
                      determineDateRange(item.id);
                    }}
                    selected={items[selected].id === item.id}
                    aria-selected={items[selected].id === item.id}
                  >
                    {item.text}
                  </DropdownLink>
                ))}
              </MinWidthList>
            )
          }
          onShow={() => {
            setShow(true);
          }}
          onHide={onHide}
        >
          <DropdownStyled
            id={triggerId}
            buttonSize="sm"
            count={0}
            iconTrailing={IconChevronDown}
            isActive={show}
          >
            {items[selected].id === DateRangeWindow.CustomDateRange
              ? dateRangeToDisplay(
                  props.createdAtRange,
                  t(
                    'atlas.deliveryTracking.table.customDateRange',
                    `Custom date range`,
                    'Select a custom date range for deliveries shown.',
                  ),
                )
              : items[selected].text}
          </DropdownStyled>
        </DropdownTriggerStyled>
        {showDateRangePicker && (
          <DateRangePickerWrapper
            createdAtRange={props.createdAtRange}
            onDateRangeChange={props.onDateRangeChange}
            onCancel={() => setShowDateRangePicker(false)}
          />
        )}
      </Container>
    </>
  );
};
